/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestRejectedInterface,
  RequestRejectedApiResponseInterface,
  RequestRejectedInitialProperties,
} from '../../interface/request-rejected-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestRejectedStation = createAsyncThunk<
  RequestRejectedApiResponseInterface, // Return type
  ApiRequestRejectedInterface
>(
  'request-rejected-station/fetchRequestRejectedStation',
  async (data: ApiRequestRejectedInterface, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestStationByStatus(
        removeEmptyKeys({ ...data, requestStatus: 'rejected' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRequestRejectedStationFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-rejected-station/fetchRejectedStationFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestStationFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestRejectedInitialProperties = {
  requestRejected: [],
  limit: 0,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const requestRejectedStationSlice = createSlice({
  name: 'request-rejected-station',
  initialState,
  reducers: {
    /**
     * Update the approved filters
     * @param {any} state - The current state of the request rejected station slice
     * @param {any} action - The payload action containing the filter update properties for the rejected station
     */
    updateRejectedStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state of the request rejected station slice
     */
    disableRejectedStationFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param {any} state - The current state of the request rejected station slice
     * @param {any} action - The payload action containing the sort field and sort order for the rejected station
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param {any} builder - The action reducer map builder for the request rejected station slice
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestRejectedInitialProperties>) => {
    builder
      .addCase(fetchRequestRejectedStation.pending, (state: RequestRejectedInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestRejectedStation.fulfilled,
        (
          state: RequestRejectedInitialProperties,
          action: PayloadAction<RequestRejectedApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestRejected = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestRejectedStation.rejected, (state: RequestRejectedInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(
        fetchRequestRejectedStationFilters.pending,
        (state: RequestRejectedInitialProperties) => {
          state.filterListLoading = true;
        },
      )
      .addCase(
        fetchRequestRejectedStationFilters.fulfilled,
        (
          state: RequestRejectedInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(
        fetchRequestRejectedStationFilters.rejected,
        (state: RequestRejectedInitialProperties) => {
          state.filterListLoading = false;
        },
      );
  },
});
export const { updateRejectedStationFilters, disableRejectedStationFilterApplied, setSortData } =
  requestRejectedStationSlice.actions;
export default requestRejectedStationSlice.reducer;
